import React, { useContext, useEffect, useState } from 'react';

import { useParams } from '@gatsbyjs/reach-router';
import { createStyles, makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import { GridRowParams, GridValueFormatterParams } from '@mui/x-data-grid';

import CustomDataGrid from '../../components/data-grid';
import JobLogButton from '../../components/job-log-button';
import JobRunButton from '../../components/job-run-button';
import Screen from '../../components/screen';
import SplashWait from '../../components/splash-wait';
import { AppContext } from '../../contexts/app-context';
import GlobalStyles from '../../global-styles';
import {
  apiRequestError, canReadPendingEnrollment, formatDateTimeForDataGrid, getBreadcrumbMaxItems,
  getPendingEnrollmentCompanyBatches, getShowDashboardValue, goToRoutePage, isNullOrWhiteSpace,
  setWindowTitle, toggleShowDashboard
} from '../../utils/helper';
import { DashboardOffIcon, DashboardOnIcon, MoreIcon, RefreshIcon } from '../../utils/icons';
import {
  Box, Breadcrumbs, Button, Card, CardContent, CardHeader, Grid, Hidden, IconButton, Link,
  ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography
} from '../../utils/material-ui-core';

const Batches = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { showAlert, pendingEnrollmentCompanies, refreshData, userFunctionAccess } = useContext(AppContext);

  const { companyKey } = useParams();

  const [gridMoreAnchorEl, setGridMoreAnchorEl] = React.useState(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [showDashboard, setShowDashboard] = useState<boolean>(true);
  const [enrollmentBatchData, setEnrollmentBatchData] = useState<PendingEnrollmentCompanyBatch[]>([
    {
      batchLoadId: 0,
      batchLoadKey: "",
      createdDate: "",
      batchTypeName: "",
      recordCount: 0,
      pendingCount: 0,
      inQueueCount: 0,
      holdCount: 0,
    },
  ]);

  const columns = [
    { field: "batchLoadId", headerName: "File Id", width: 150 },
    {
      field: "createdDate",
      headerName: "Created Date",
      type: "dateTime",
      width: 250,
      valueFormatter: (params: GridValueFormatterParams) => formatDateTimeForDataGrid(params.value as string),
    },
    { field: "batchTypeName", headerName: "Batch Type", width: 180 },
    {
      field: "recordCount",
      headerName: "# of Records",
      width: 170,
      type: "number",
    },
    {
      field: "pendingCount",
      headerName: "Pending #",
      width: 150,
      type: "number",
    },
    {
      field: "inQueueCount",
      headerName: "In Queue #",
      width: 150,
      type: "number",
    },
    { field: "holdCount", headerName: "On Hold #", width: 150, type: "number" },
    { field: "fileName", headerName: "File Name", width: 350 },
    {
      field: "batchLoadKey",
      headerName: "Batch Load Key",
      width: 250,
      hide: true,
    },
  ];

  const handleGridMoreMenuClick = (event) => {
    setGridMoreAnchorEl(event.currentTarget);
  };

  const handleGridMoreMenuClose = () => {
    setGridMoreAnchorEl(null);
  };

  const StyledMenuItem = withStyles((theme) => ({
    root: {
      "&:focus": {
        backgroundColor: theme.palette.secondary.main,
        "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
          color: theme.palette.common.white,
        },
      },
    },
  }))(MenuItem);

  useEffect(() => {
    setWindowTitle("Enrollment Correction - Companies");
    window.scrollTo(0, 0);
  }, []);

  const gotoCompany = () => {
    goToRoutePage("enrollmentcorrection/companies/");
  };

  const refresh = async () => {
    setLoading(true);
    await refreshData("pec");
    getData();
  };

  const getData = async () => {
    try {
      setLoading(true);
      setEnrollmentBatchData([]);

      const apiResponse = await getPendingEnrollmentCompanyBatches(companyKey);

      if (apiResponse.success) {
        if (apiResponse.data.pendingEnrollmentCompanyBatches.length === 0) {
          showAlert({
            message: "No row found.",
            severity: "info",
          });
          await refreshData("pec");
          goToRoutePage("enrollmentcorrection/companies");
          return;
        }

        setEnrollmentBatchData(apiResponse.data.pendingEnrollmentCompanyBatches);
        setLoading(false);
      } else {
        setLoading(false);
        showAlert({ message: apiResponse.errorMessage, severity: "error" });
      }
    } catch (e) {
      setLoading(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  const handleDataGridClick = async (params: GridRowParams, event: any) => {
    goToRoutePage("enrollmentcorrection/companies/" + companyKey + "/batches/" + params.row.batchLoadKey + "/" + params.row.batchLoadId);
  };

  useEffect(() => {
    setWindowTitle("Enrollment Correction - Batches");
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const initialPageAsync = async () => {
      if (!canReadPendingEnrollment(userFunctionAccess)) {
        showAlert({ message: "Access Denied.", severity: "error" });
        goToRoutePage("home");
        return;
      }

      if (isNullOrWhiteSpace(companyKey)) {
        showAlert({ message: "Company key missing.", severity: "error" });
        goToRoutePage("home");
        return;
      }

      setShowDashboard(getShowDashboardValue("ShowEnrollmentBatchesDashboard", true));

      getData();
    };

    initialPageAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyKey]);

  return (
    <>
      {loading ? (
        <SplashWait />
      ) : (
        <Screen>
          <Box className={pageStyles.root}>
            <Grid container spacing={3}>
              <Grid item xs={9}>
                <Box className={globalStyles.bottomSpacer}>
                  <Breadcrumbs maxItems={getBreadcrumbMaxItems(4)}>
                    <Link color="inherit" onClick={() => goToRoutePage("home")} className={globalStyles.link}>
                      Home
                    </Link>
                    <Typography color="textPrimary">Enrollment Correction</Typography>
                    <Link color="inherit" onClick={() => gotoCompany()} className={globalStyles.link}>
                      Companies
                    </Link>
                    {pendingEnrollmentCompanies
                      .filter((pendingEnrollmentCompany) => pendingEnrollmentCompany.companyKey === companyKey)
                      .map((pendingEnrollmentCompany, index) => (
                        <Typography color="textPrimary" key={index}>
                          {pendingEnrollmentCompany.companyName}
                        </Typography>
                      ))}
                    <Typography color="textPrimary">Batches</Typography>
                  </Breadcrumbs>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Hidden xsDown>
                  <Box display="flex" justifyContent="flex-end" width={1}>

                    <IconButton onClick={() => setShowDashboard(toggleShowDashboard("ShowEnrollmentBatchesDashboard", showDashboard))}>
                      <Tooltip arrow title={!showDashboard ? "Show Dashboard" : "Hide Dashboard"}>
                        {!showDashboard ? <DashboardOffIcon color="secondary" /> : <DashboardOnIcon color="secondary" />}
                      </Tooltip>
                    </IconButton>

                    <JobLogButton jobPrefix='enrollment' jobName='Enrollment' mode={0} handleGridMoreMenuClose={() => { }} />

                    <JobRunButton jobName={"Enrollment"} mode={0} fileId={0} />

                    <IconButton>
                      <Tooltip arrow title={"Refresh"}>
                        <RefreshIcon color="secondary" onClick={() => refresh()} />
                      </Tooltip>
                    </IconButton>
                  </Box>
                </Hidden>

                <Hidden smUp>
                  <Box display="flex" justifyContent="flex-end" width={1}>
                    <Tooltip arrow title="Menu">
                      <Button color="secondary" aria-controls="fade-menu" aria-haspopup="true" onClick={handleGridMoreMenuClick}>
                        <MoreIcon />
                      </Button>
                    </Tooltip>
                    <Menu elevation={2} id="grid-menu" anchorEl={gridMoreAnchorEl} keepMounted open={Boolean(gridMoreAnchorEl)} onClose={handleGridMoreMenuClose}>

                      <StyledMenuItem
                        onClick={(e) => {
                          setShowDashboard(toggleShowDashboard("ShownEnrollmentDashboard", showDashboard))
                          handleGridMoreMenuClose();
                        }}
                      >
                        <ListItemIcon>
                          {!showDashboard ? <DashboardOffIcon fontSize="small" /> : <DashboardOnIcon fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText primary={!showDashboard ? "Show Dashboard" : "Hide Dashboard"} />
                      </StyledMenuItem>

                      <JobLogButton jobPrefix='enrollment' jobName='Enrollment' mode={1} handleGridMoreMenuClose={handleGridMoreMenuClose} />

                      <JobRunButton jobName={"Enrollment"} mode={2} fileId={0} />

                      <StyledMenuItem
                        onClick={(e) => {
                          refresh();
                          handleGridMoreMenuClose();
                        }}
                      >
                        <ListItemIcon>
                          <RefreshIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={"Refresh "} />
                      </StyledMenuItem>
                    </Menu>
                  </Box>
                </Hidden>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={12} md={showDashboard ? 9 : 12} lg={showDashboard ? 10 : 12}>
                <Box className={pageStyles.datagridContainer}>
                  <CustomDataGrid uniqueGridCode={"PendingEnrollmentCompanyBatchResult"} columns={columns} rows={enrollmentBatchData} showToolBar={true} sortModel={[{ field: "batchLoadId", sort: "asc" }]} handleDataGridClick={handleDataGridClick} />
                </Box>
              </Grid>
              {
                showDashboard ?
                  <Grid item xs={12} sm={12} md={3} lg={2}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={7} lg={12}>
                        <Card elevation={0} className={globalStyles.rightCardContainer}>
                          <CardHeader title="" subheader="INCLUDES" />
                          <CardContent>
                            <Grid container>
                              <Grid item xs={6} sm={6} md={4} lg={12}>
                                <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                                  <Box>
                                    <Typography variant="h6" align="center" className="count">
                                      {enrollmentBatchData.length}
                                    </Typography>
                                  </Box>
                                  <Box >
                                    <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                      Files
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                              <Grid item xs={6} sm={6} md={4} lg={12}>
                                <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                                  <Box >
                                    <Typography variant="h6" align="center" className="count">
                                      {enrollmentBatchData.length > 0 ? (
                                        <>
                                          {enrollmentBatchData
                                            .map(function (enrollmentBatch) {
                                              return enrollmentBatch.recordCount;
                                            })
                                            .reduce(function (totalCount, count) {
                                              return totalCount + count;
                                            })}
                                        </>
                                      ) : (
                                        0
                                      )}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" align="center" className={globalStyles.toggleFontColor}>
                                      Records
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                              <Grid item xs={6} sm={6} md={4} lg={12}>
                                <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                                  <Box >
                                    <Typography variant="h6" align="center" className="count">
                                      {enrollmentBatchData.length > 0 ? (
                                        <>
                                          {enrollmentBatchData
                                            .map(function (enrollmentBatch) {
                                              return enrollmentBatch.pendingCount;
                                            })
                                            .reduce(function (totalCount, count) {
                                              return totalCount + count;
                                            })}
                                        </>
                                      ) : (
                                        0
                                      )}
                                    </Typography>

                                  </Box>
                                  <Box >
                                    <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                      Pending
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                              <Grid item xs={6} sm={6} md={4} lg={12}>
                                <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                                  <Box >
                                    <Typography variant="h6" align="center" className="count">
                                      {enrollmentBatchData.length > 0 ? (
                                        <>
                                          {enrollmentBatchData
                                            .map(function (enrollmentBatch) {
                                              return enrollmentBatch.inQueueCount;
                                            })
                                            .reduce(function (totalCount, count) {
                                              return totalCount + count;
                                            })}
                                        </>
                                      ) : (
                                        0
                                      )}
                                    </Typography>
                                  </Box>
                                  <Box >
                                    <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                      In Queue
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                              <Grid item xs={6} sm={6} md={4} lg={12}>
                                <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                                  <Box >
                                    <Typography variant="h6" align="center" className="count">
                                      {enrollmentBatchData.length > 0 ? (
                                        <>
                                          {enrollmentBatchData
                                            .map(function (enrollmentBatch) {
                                              return enrollmentBatch.holdCount;
                                            })
                                            .reduce(function (totalCount, count) {
                                              return totalCount + count;
                                            })}
                                        </>
                                      ) : (
                                        0
                                      )}
                                    </Typography>
                                  </Box>
                                  <Box >
                                    <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                      On Hold
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                        <Box p="3">&nbsp;</Box>
                        <Box p="3">&nbsp;</Box>
                      </Grid>
                    </Grid>
                  </Grid>
                  : null
              }
            </Grid>
          </Box>
        </Screen>
      )}
    </>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: 0,
    },
    datagridContainer: {
      height: "70vh",
      width: "100%",
      marginBottom: theme.spacing(2),
    },
    dashboardCount: {
      fontSize: 20,
      fontWeight: "bold",
    },
  })
);

export default Batches;
